import React from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { Toggle, WarningMessage } from '../../../components/widgets';
import Table from '../../../components/organisms/Table';
import TableCell from '../../../components/widgets/TableCell';
import FormInnerSection from '../FormInnerSection';
import { ATTRIBUTION_TAB_IDS, MACRO_INPUT_TYPES, TRANSLATE_PREFIX } from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import { useTabUpdater } from '../../formUpdaters';
import AppLevelMacrosList from './AppLevelMacrosList';
import css from './AppLevelMacroSection.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;

const TABLE_METADATA = [
    {
        name: 'parameter_key_name',
        displayName: `${TAB_PREFIX}.APP_LEVEL_MACRO_TABLE.PARAMETER_KEY_NAME`,
        cellComponent: TableCell,
        cellProps: { textClassName: css.parameterKey },
    },
    {
        name: 'field_name',
        displayName: `${TAB_PREFIX}.APP_LEVEL_MACRO_TABLE.FIELD_NAME`,
        cellComponent: TableCell,
        cellProps: { textClassName: css.placeholderCell },
    },
    {
        name: 'description',
        displayName: `${TAB_PREFIX}.APP_LEVEL_MACRO_TABLE.DESCRIPTION`,
        cellComponent: TableCell,
        cellProps: { textClassName: css.placeholderCell },
    },
];

const AppLevelMacroSection = ({ readOnly = false, translate }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const tabData = values.formData?.[TAB_ID] || {};

    const { confirm, dialog } = useConfirmDialog();

    const sampleHint = translate(`${TAB_PREFIX}.APP_LEVEL_MACRO_TABLE.SAMPLE_HINT`);
    const tableData = [
        {
            key: 'app_id',
            parameter_key_name: 'app_id',
            field_name: sampleHint,
            description: sampleHint,
        },
    ];

    const hasData = () => tabData.app_level_macros?.length > 0;

    const disable = () => updateTab({ needs_app_level_macro: false, app_level_macros: undefined });

    const handleToggle = e => {
        const { checked } = e.target;
        if (!checked && hasData()) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_APP_LEVEL_MACRO_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_APP_LEVEL_MACRO_TEXT`,
                onConfirm: disable,
            });
            return;
        }
        const seedMacros = checked && !(tabData.app_level_macros || []).length;
        updateTab({
            needs_app_level_macro: checked,
            ...(seedMacros ? { app_level_macros: [{ input_type: MACRO_INPUT_TYPES.TEXT }] } : {}),
        });
    };

    return (
        <>
            <FormInnerSection
                titleKey={`${TAB_PREFIX}.NEEDS_APP_LEVEL_MACRO_LABEL`}
                descriptionKey={`${TAB_PREFIX}.NEEDS_APP_LEVEL_MACRO_SUBTITLE`}
                headerRight={
                    <Toggle checked={!!tabData.needs_app_level_macro} onToggle={handleToggle} disabled={readOnly} />
                }
            >
                {tabData.needs_app_level_macro && (
                    <>
                        <WarningMessage
                            type="info"
                            showIcon={false}
                            collapsible
                            collapsibleTitle={`${TAB_PREFIX}.APP_LEVEL_MACRO_INFO_TITLE`}
                            message={
                                <div className={css.infoContent}>
                                    <code className={css.urlExample}>
                                        <Translate id={`${TAB_PREFIX}.APP_LEVEL_MACRO_URL_EXAMPLE`} />
                                    </code>
                                    <Table
                                        metadata={TABLE_METADATA}
                                        data={tableData}
                                        zebraTable={false}
                                        rowHoverEnabled={false}
                                        showInnerBorder={false}
                                        tableClass={css.fieldsTable}
                                    />
                                    <div className={css.fieldsExplanation}>
                                        <Translate id={`${TAB_PREFIX}.APP_LEVEL_MACRO_FIELDS_EXPLANATION`} />
                                    </div>
                                </div>
                            }
                        />
                        <AppLevelMacrosList readOnly={readOnly} />
                    </>
                )}
            </FormInnerSection>
            {dialog}
        </>
    );
};

AppLevelMacroSection.propTypes = {
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AppLevelMacroSection);
